import { CartLinesUpdateEvent } from '@shopify/events';

class NoorFreeCase extends HTMLElement {
  connectedCallback() {
    this.caseVariantId = this.dataset.caseVariantId;
    this.qualifyingProductIds = new Set((this.dataset.qualifyingProductIds || '').split(',').filter(Boolean));
    this.busy = false;
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    document.addEventListener('shopify:cart:lines-update', this.handleCartUpdate);
    this.sync();
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:cart:lines-update', this.handleCartUpdate);
  }

  handleCartUpdate(event) {
    event.promise?.then(() => this.sync()).catch(() => {});
  }

  async sync() {
    if (this.busy) return;

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, { credentials: 'same-origin' });
      if (!response.ok) return;
      await this.reconcile(await response.json());
    } catch (_) {}
  }

  async reconcile(cart) {
    const giftLines = cart.items.filter((item) => String(item.variant_id) === this.caseVariantId);
    const hasQualifyingProduct = cart.items.some((item) => this.isQualifyingProduct(item));

    if (!hasQualifyingProduct) {
      for (const line of giftLines) await this.changeLine(line.key, 0);
      return;
    }

    if (giftLines.length === 0) {
      await this.addGift();
      return;
    }

    if (giftLines[0].quantity !== 1) await this.changeLine(giftLines[0].key, 1);
    for (const line of giftLines.slice(1)) await this.changeLine(line.key, 0);
  }

  isQualifyingProduct(item) {
    return (
      this.qualifyingProductIds.has(String(item.product_id)) ||
      item.product_type === 'Sonnenbrille' ||
      item.product_type === 'Blaulichtfilterbrille'
    );
  }

  async addGift() {
    await this.mutateCart({
      action: 'add',
      lines: [{ merchandiseId: this.caseVariantId, quantity: 1 }],
      payload: {
        items: [
          {
            id: Number(this.caseVariantId),
            quantity: 1,
            properties: { _noor_free_case: 'true' },
          },
        ],
      },
    });
  }

  async changeLine(key, quantity) {
    await this.mutateCart({
      action: quantity === 0 ? 'remove' : 'update',
      lines: [{ id: key, quantity }],
      payload: { id: key, quantity },
    });
  }

  async mutateCart({ action, lines, payload }) {
    if (this.busy) return;
    this.busy = true;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'standard-action',
        lines,
        promise: deferred.promise,
        detail: { source: 'noor-free-case' },
      })
    );

    try {
      const endpoint = action === 'add' ? 'cart/add.js' : 'cart/change.js';
      const response = await fetch(`${window.Shopify.routes.root}${endpoint}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Cart gift request failed with ${response.status}`);

      const cart = await response.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: 'noor-free-case', items: cart.items },
      });
    } catch (error) {
      deferred.reject(error);
    } finally {
      this.busy = false;
    }
  }
}

if (!customElements.get('noor-free-case')) {
  customElements.define('noor-free-case', NoorFreeCase);
}
